import { apiClient, axiosInstance } from '../lib/api/client';
import {
  buildProductImagesFormData,
  buildReviewFormData,
} from '../utils/uploadFormData';

// API d'authentification
export const authAPI = {
  // Connexion
  login: (credentials) => apiClient.post('/users/login', credentials),
  
  // Inscription
  register: (userData) => apiClient.post('/auth/register', userData),
  registerClient: (clientData) => apiClient.post('/users/clients', clientData),
  
  // Déconnexion
  logout: () => apiClient.post('/auth/logout'),
  
  // Rafraîchir le token
  refreshToken: () => apiClient.post('/auth/refresh'),
  
  // Profil utilisateur
  getProfile: () => apiClient.get('/users/me'),
  
  // Mettre à jour le profil
  updateProfile: (userId, data) => apiClient.put(`/users/${userId}`, data),

  // Obtenir un utilisateur par ID
  getUser: (userId) => apiClient.get(`/users/${userId}`),
  
  // Obtenir les détails d'un utilisateur
  getUserDetails: (userId) => apiClient.get(`/users/${userId}/details`),
  
  // Obtenir la liste des utilisateurs
  getUsersList: (params = {}) => apiClient.get('/users/list', { params }),
  
  // Obtenir les statistiques des utilisateurs
  getUsersStats: () => apiClient.get('/users/stats'),
  
  // Basculer le statut d'un utilisateur (suspendre/activer)
  toggleUserStatus: (userId) => apiClient.patch(`/users/${userId}/toggle-status`),
  
  // Réinitialiser le mot de passe d'un utilisateur (admin)
  resetUserPassword: (userId) => apiClient.post(`/users/${userId}/reset-password`),
  
  // Mot de passe oublié
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  
  // Vérifier le token de réinitialisation (path, pour dashboard/vendor)
  verifyResetToken: (token) => apiClient.get(`/auth/verify-reset-token/${token}`),

  // Valider le token (query, pour client) — GET /auth/validate-reset-token?token=xxx
  validateResetToken: (token) => apiClient.get('/auth/validate-reset-token', { params: { token } }),

  // Réinitialiser le mot de passe (path + body, pour dashboard)
  resetPassword: (token, data) => apiClient.post(`/auth/reset-password/${token}`, data),

  // Réinitialiser le mot de passe (body seul, pour client) — POST /auth/reset-password { email, code, newPassword }
  resetPasswordWithBody: (data) => apiClient.post('/auth/reset-password', data),
  
  // Changer le mot de passe (utilisateur connecté)
  changePassword: (data) => apiClient.put('/users/me/password', data),
  
  // Authentification Google
  loginWithGoogle: (token) => apiClient.post('/auth/google', { token }),
};

// API Newsletter
export const newsletterAPI = {
  subscribe: (email, recaptchaToken) =>
    apiClient.post('/newsletter/subscribe', { email, recaptchaToken }),
};

// API FAQ / contact
export const faqAPI = {
  /** POST /faq/messages — { fullName, email, phone?, message } */
  sendMessage: (payload) => apiClient.post('/faq/messages', payload),
  /** GET /faq/messages — Liste des messages (admin) */
  getMessages: (filters = {}) => apiClient.get('/faq/messages', { params: filters }),
  /** PATCH /faq/messages/:id/read — Marquer comme lu */
  markAsRead: (id) => apiClient.patch(`/faq/messages/${id}/read`),
};

// API des commandes
export const shopsAPI = {

  // Obtenir les boutiques les plus performantes
  getShopTopPerformers: () => apiClient.get('/shops/top-by-products'),

  // Suspendre une boutique (motif envoyé au backend)
  suspendShop: (id, data) => apiClient.patch(`/shops/${id}/suspend`, data || {}),

  // Obtenir les statistiques des boutiques
  getShopsStats: () => apiClient.get('/shops/statistics'),

  // Obtenir une boutique par slug
  getShopBySlug: (slug) => apiClient.get(`/shops/slug/${slug}`),

  // Obtenir toutes les boutiques
  getShops: (params = {}) => apiClient.get('/shops', { params }),
  
  // Obtenir une boutique par ID
  getShop: (id) => apiClient.get(`/shops/${id}`),

  // Obtenir les documents uploadés lors de la création de la boutique
  getShopDocuments: (id) => apiClient.get(`/shops/${id}/documents`),

  // Créer une nouvelle boutique
  createShop: (shopData) => apiClient.post('/shops/create', shopData),
  
  // Mettre à jour une boutique
  updateShop: (id, shopData) => {
    // Si shopData est un FormData, utiliser multipart/form-data
    if (shopData instanceof FormData) {
      return apiClient.put(`/shops/${id}`, shopData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return apiClient.put(`/shops/${id}`, shopData);
  },
  
  // Mettre à jour le statut d'une boutique
  updateShopStatus: (id, status) => apiClient.patch(`/shops/${id}/status`, { status }),

  // Valider une boutique
  validateShop: (id) => apiClient.patch(`/shops/${id}/validate`),

  // Refuser une boutique
  rejectShop: (id, rejectionReason) => apiClient.patch(`/shops/${id}/reject`, { rejectionReason }),
  
  // Supprimer une commande
  deleteShop: (id) => apiClient.delete(`/shops/${id}`),
};

// API des commandes
export const ordersAPI = {
  // Obtenir toutes les commandes
  getOrders: (params = {}) => apiClient.get('/orders', { params }),

  // Admin — Obtenir toutes les commandes (paginated + filtres)
  // GET /orders/all?statut=&startDate=&endDate=&page=1&limit=10&sortBy=&sortOrder=
  getAllOrdersAdmin: (params = {}) =>
    apiClient.get('/orders/all', { params: { page: 1, limit: 10, ...params } }),
  
  // Obtenir les statistiques des commandes (boutique)
  getOrderStatistics: (shopId) => apiClient.get(`/orders/statistics?shopId=${shopId}`),

  // Admin — Statistiques commandes plateforme (tous vendeurs)
  getPlatformOrderStatistics: () => apiClient.get('/orders/statistics/platform'),
  
  // Obtenir les statistiques du tableau de bord
  getDashboardStats: (shopId) => apiClient.get(`/orders/dashboard/${shopId}`),

  // Obtenir les commandes d'une boutique (paginated) — GET /orders/shop/:shopId?page=1&limit=10&statut=...
  getOrdersByShop: (shopId, params = {}) =>
    apiClient.get(`/orders/shop/${shopId}`, { params: { page: 1, limit: 10, ...params } }),
  
  // Obtenir une commande par ID
  getOrder: (id) => apiClient.get(`/orders/${id}`),
  
  // Créer une nouvelle commande (utilisateur connecté, panier côté serveur)
  createOrder: (orderData) => apiClient.post('/orders', orderData),

  /**
   * Créer une commande invité (sans compte).
   * Payload: { items, customer: { firstName, lastName, email, phone, address }, fraisLivraison, adresseLivraisonId? }
   * Le backend doit exposer POST /orders/guest ou accepter ce format sur POST /orders.
   */
  createGuestOrder: (orderData) => apiClient.post('/orders/guest', orderData),
  
  // Mettre à jour une commande
  updateOrder: (id, orderData) => apiClient.put(`/orders/${id}`, orderData),
  
  // Mettre à jour le statut d'une commande
  updateOrderStatus: (id, statut) => apiClient.put(`/orders/${id}/status`, { statut }),

  // Mettre à jour le statut de paiement (paid / unpaid / failed / refunded)
  updateOrderPaymentStatus: (id, paymentStatus) =>
    apiClient.put(`/orders/${id}/payment-status`, { paymentStatus }),
  
  // Supprimer une commande
  deleteOrder: (id) => apiClient.delete(`/orders/${id}`),
};

// API codes promo (boutiques)
export const promoCodesAPI = {
  validate: (payload) => apiClient.post('/promo-codes/validate', payload),
  create: (data) => apiClient.post('/promo-codes', data),
  listByShop: (shopId) => apiClient.get(`/promo-codes/shop/${shopId}`),
  getById: (id) => apiClient.get(`/promo-codes/${id}`),
  update: (id, data) => apiClient.patch(`/promo-codes/${id}`, data),
  remove: (id) => apiClient.delete(`/promo-codes/${id}`),
};

// API des produits
export const productsAPI = {
  // Obtenir les statistiques des produits
  getProductsStatistics: () => apiClient.get('/products/statistics'),
  
  // Obtenir les statistiques des produits par boutique
  getProductsStatsByShop: (shopId) => apiClient.get(`/products/statistics?shopId=${shopId}`),

  // Obtenir les produits similaires
  getSimilarProducts: (slug, filters = {}) => apiClient.get(`/products/slug/${slug}/similar`, { params: filters }),

  // Obtenir un produit par slug
  getProductBySlug: (slug) => apiClient.get(`/products/slug/${slug}`),

  // Obtenir les produits par boutique par slug
  getProductsByShopSlug: (shopSlug, params = {}) => apiClient.get(`/products/shop-slug/${shopSlug}`, { params }),

  // Obtenir tous les produits
  getProducts: (params = {}) => apiClient.get('/products', { params }),

  // Obtenir les produits par boutique
  getProductsByShop: (shopId, params = {}) => apiClient.get(`/products/shop/${shopId}`, { params }),
  
  // Obtenir un produit par ID
  getProduct: (id) => apiClient.get(`/products/${id}`),
  
  // Créer un nouveau produit
  createProduct: (productData) => apiClient.post('/products/create', productData),
  
  // Mettre à jour un produit
  updateProduct: (id, productData) => apiClient.put(`/products/${id}`, productData),
  
  // Supprimer un produit (réponse peut contenir productInCarts, message, usersWithProductInCart, cartItemsCount)
  deleteProduct: (id) => apiClient.delete(`/products/${id}`),

  // Suppression forcée quand le produit est dans des paniers
  deleteProductForce: (id) => apiClient.delete(`/products/${id}/force`),
  
  // Upload d'une ou plusieurs images produit (POST /products/:id/images, champ "images")
  uploadProductImages: (id, imageFiles, onProgress) => {
    const files = Array.isArray(imageFiles) ? imageFiles : [imageFiles];
    const formData = buildProductImagesFormData(files);
    return apiClient.upload(`/products/${id}/images`, formData, onProgress);
  },

  // Upload d'image de produit
  uploadProductImage: (id, imageFile, onProgress) => {
    const formData = buildProductImagesFormData([imageFile]);
    return apiClient.upload(`/products/${id}/images`, formData, onProgress);
  },

  // Supprimer des images de produit
  deleteProductImages: (id, imageUrls) => apiClient.delete(`/products/${id}/images`, { data: { imageUrls } }),

  // Créer un avis pour un produit (FormData si photos, sinon JSON)
  createProductReview: (reviewData, files = []) => {
    const hasFiles = Array.isArray(files) && files.length > 0;
    if (hasFiles) {
      const formData = buildReviewFormData(reviewData, files);
      return apiClient.upload('/reviews', formData);
    }
    return apiClient.post('/reviews', reviewData);
  },

  // Mettre à jour son avis (note + commentaire)
  updateProductReview: (id, reviewData) =>
    apiClient.put(`/reviews/${id}`, reviewData),

  // Obtenir les avis d'un produit
  getProductReviews: (productId) => apiClient.get(`/reviews/product/${productId}`),

  // Éligibilité à laisser un avis
  getReviewEligibility: (productId) =>
    apiClient.get(`/reviews/eligibility/${productId}`),

  // Admin — liste avis
  getAdminReviews: (params = {}) => apiClient.get('/reviews/admin', { params }),

  // Vendeur — avis de ses produits
  getShopReviews: (params = {}) => apiClient.get('/reviews/shop', { params }),

  // Admin — modérer
  moderateReview: (id, payload) =>
    apiClient.patch(`/reviews/admin/${id}/moderate`, payload),

  // Marquer un avis comme utile
  likeReview: (id) => apiClient.post(`/reviews/${id}/like`),

  // Retirer le marquage utile
  unlikeReview: (id) => apiClient.delete(`/reviews/${id}/like`),

  // Liker un produit
  likeProduct: (id) => apiClient.post(`/products/${id}/like`),

  // Retirer un like sur un produit
  unlikeProduct: (id) => apiClient.delete(`/products/${id}/like`),

  // Produits likés par l'utilisateur courant
  getLikedProducts: () => apiClient.get('/products/liked'),
};

// API du dashboard
export const dashboardAPI = {
  // Statistiques du dashboard
  getStats: () => apiClient.get('/dashboard/stats'),
  
  // Ventes par période
  getSales: (period = 'month') => apiClient.get(`/dashboard/sales?period=${period}`),
  
  // Produits les plus vendus
  getBestSellingProducts: () => apiClient.get('/dashboard/best-selling'),
  
  // Ventes par localisation
  getSalesByLocation: () => apiClient.get('/dashboard/sales-by-location'),
  
  // Catégories principales
  getCategories: () => apiClient.get('/dashboard/categories'),
};

// API des catégories
export const categoriesAPI = {
  // Obtenir toutes les catégories avec le nombre de produits
  getCategoriesWithProductsCount: (filters = {}) => apiClient.get('/categories/with-product-count', { params: filters }),

  // Obtenir toutes les catégories
  getCategories: () => apiClient.get('/categories'),

  // Sous-catégories d'une catégorie parente
  getCategoryChildren: (parentId) => apiClient.get(`/categories/${parentId}/children`),

  // Obtenir une catégorie par ID
  getCategory: (id) => apiClient.get(`/categories/${id}`),
  
  // Créer une nouvelle catégorie
  createCategory: (categoryData) => apiClient.post('/categories/create', categoryData),
  
  // Mettre à jour une catégorie
  updateCategory: (id, categoryData) => apiClient.put(`/categories/${id}`, categoryData),
  
  // Supprimer une catégorie
  deleteCategory: (id) => apiClient.delete(`/categories/${id}`),
};

// API des bannières
export const bannersAPI = {
  // Obtenir toutes les bannières (optionnellement filtrées par type)
  // Accepte une chaîne (ex: 'BANNIERE') ou un objet (ex: { type: 'BANNIERE', isActive: true })
  getBanners: (filters = {}) => {
    const params = typeof filters === 'string' ? { type: filters } : { ...(filters || {}) };
    return apiClient.get('/banners', { params });
  },
  
  // Obtenir une bannière par ID
  getBanner: (id) => apiClient.get(`/banners/${id}`),
  
  // Créer une nouvelle bannière (avec upload d'image)
  createBanner: (formData) => apiClient.upload('/banners', formData),
  
  // Mettre à jour une bannière (avec upload d'image)
  updateBanner: (id, formData) => {
    // Pour PUT avec FormData, on doit utiliser axiosInstance directement
    return axiosInstance.put(`/banners/${id}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  
  // Mettre à jour le statut d'une bannière
  updateBannerStatus: (id, status) => apiClient.patch(`/banners/${id}`, { status }),
  
  // Mettre à jour la position d'une bannière
  updateBannerPosition: (id, position) => apiClient.patch(`/banners/${id}`, { position }),
  
  // Activer une bannière
  activateBanner: (id) => apiClient.put(`/banners/${id}/activate`),
  
  // Désactiver une bannière
  deactivateBanner: (id) => apiClient.put(`/banners/${id}/deactivate`),
  
  // Supprimer une bannière
  deleteBanner: (id) => apiClient.delete(`/banners/${id}`),
};

// API des clients
export const clientsAPI = {
  // Obtenir tous les clients
  getClients: (params = {}) => apiClient.get('/clients', { params }),
  
  // Obtenir un client par ID
  getClient: (id) => apiClient.get(`/clients/${id}`),
  
  // Créer un nouveau client
  createClient: (clientData) => apiClient.post('/clients', clientData),
  
  // Mettre à jour un client
  updateClient: (id, clientData) => apiClient.put(`/clients/${id}`, clientData),
  
  // Supprimer un client
  deleteClient: (id) => apiClient.delete(`/clients/${id}`),
};

// API du panier
export const cartAPI = {
  // Ajouter un article au panier
  addCartItem: (userId, itemData) => apiClient.post(`/users/${userId}/cart/items`, itemData),
  
  // Obtenir les articles du panier
  getCartItems: (userId) => apiClient.get(`/users/${userId}/cart`),
  
  // Mettre à jour un article du panier
  updateCartItem: (userId, itemId, itemData) => apiClient.put(`/users/${userId}/cart/items/${itemId}`, itemData),
  
  // Supprimer un article du panier
  deleteCartItem: (userId, itemId) => apiClient.delete(`/users/${userId}/cart/items/${itemId}`),

  // Vider entièrement le panier
  clearCart: (userId) => apiClient.delete(`/users/${userId}/cart/clear`),
};

// API des vendeurs
export const vendorsAPI = {
  // Obtenir tous les vendeurs
  getVendors: (params = {}) => apiClient.get('/vendors', { params }),
  
  // Obtenir un vendeur par ID
  getVendor: (id) => apiClient.get(`/vendors/${id}`),
  
  // Créer un nouveau vendeur
  createVendor: (vendorData) => apiClient.post('/vendors', vendorData),
  
  // Mettre à jour un vendeur
  updateVendor: (id, vendorData) => apiClient.put(`/vendors/${id}`, vendorData),
  
  // Mettre à jour le statut d'un vendeur
  updateVendorStatus: (id, status) => apiClient.patch(`/vendors/${id}/status`, { status }),
  
  // Supprimer un vendeur
  deleteVendor: (id) => apiClient.delete(`/vendors/${id}`),
  
  // Obtenir les statistiques des vendeurs
  getVendorStats: () => apiClient.get('/vendors/stats'),
};

// API des statistiques
export const statsAPI = {
  // Statistiques générales
  getGeneralStats: () => apiClient.get('/stats/general'),
  
  // Statistiques des ventes
  getSalesStats: (period = 'month') => apiClient.get(`/stats/sales?period=${period}`),
  
  // Statistiques des produits
  getProductsStats: () => apiClient.get('/stats/products'),
  
  // Statistiques des clients
  getClientsStats: () => apiClient.get('/stats/clients'),
  
  // Statistiques des revenus
  getRevenueStats: (period = 'month') => apiClient.get(`/stats/revenue?period=${period}`),
};

// API des paiements
export const paymentsAPI = {
  // Obtenir toutes les méthodes de paiement disponibles sur la plateforme
  getAllPaymentMethods: () => apiClient.get('/payment-methods'),
  
  // Obtenir les méthodes de paiement sélectionnées par une boutique
  getShopPaymentMethods: (shopId) => apiClient.get(`/shops/${shopId}/payment-methods`),
  
  // Mettre à jour les méthodes de paiement préférées d'une boutique
  updateShopPaymentMethods: (shopId, methods) => apiClient.put(`/shops/${shopId}/payment-methods`, methods),
  
  // Obtenir l'historique des paiements
  getPaymentHistory: (params = {}) => apiClient.get('/payments/history', { params }),
};

// API des rôles
export const rolesAPI = {
  // Créer un nouveau rôle pour une boutique
  createRole: (roleData) => apiClient.post('/roles', roleData),

  // Obtenir les rôles d'une boutique
  getShopRoles: (shopId) => apiClient.get(`/roles/shop/${shopId}`),

  // Obtenir un rôle par ID
  getRole: (roleId) => apiClient.get(`/roles/${roleId}`),

  // Mettre à jour un rôle
  updateRole: (roleId, roleData) => apiClient.put(`/roles/${roleId}`, roleData),

  // Supprimer un rôle
  deleteRole: (roleId) => apiClient.delete(`/roles/${roleId}`),
};

// API des rôles admin — protégée par JwtAuthGuard + RolesGuard(ADMIN)
export const adminRolesAPI = {
  // Créer un rôle admin
  createRole: (roleData) => apiClient.post('/admin/roles', roleData),

  // Lister les rôles admin
  getAll: () => apiClient.get('/admin/roles'),

  // Détail d'un rôle admin
  getOne: (roleId) => apiClient.get(`/admin/roles/${roleId}`),

  // Mettre à jour nom / permissions
  update: (roleId, roleData) => apiClient.put(`/admin/roles/${roleId}`, roleData),

  // Soft delete
  remove: (roleId) => apiClient.delete(`/admin/roles/${roleId}`),
};

// API des utilisateurs admin — protégée par JwtAuthGuard + RolesGuard(ADMIN)
export const adminUsersAPI = {
  // Créer un admin
  create: (userData) => apiClient.post('/admin/users', userData),

  // Lister tous les admins
  getAll: () => apiClient.get('/admin/users'),

  // Détail d'un admin
  getOne: (userId) => apiClient.get(`/admin/users/${userId}`),

  // Mettre à jour infos + éventuellement roleId
  update: (userId, userData) => apiClient.put(`/admin/users/${userId}`, userData),

  // Soft delete (protection dernier admin côté back)
  remove: (userId) => apiClient.delete(`/admin/users/${userId}`),
};

// API des utilisateurs de boutique
export const shopUsersAPI = {
  // Créer un nouvel utilisateur seller pour une boutique
  createSellerUser: (userData) => apiClient.post('/users/sellers', userData),
  
  // Obtenir les utilisateurs d'une boutique
  getShopUsers: (shopId) => apiClient.get(`/users/shop/${shopId}`),
  
  // Obtenir un utilisateur par ID
  getShopUser: (shopId, userId) => apiClient.get(`/shops/${shopId}/users/${userId}`),
  
  // Mettre à jour un utilisateur
  updateShopUser: (shopId, userId, userData) => apiClient.put(`/users/${userId}/shop`, userData),
  
  // Supprimer un utilisateur
  deleteShopUser: (shopId, userId) => apiClient.delete(`/users/${userId}/shop`),
};

// API des documents légaux
export const documentsAPI = {
  // Obtenir un document légal par type (CGU, CGV, RGPD)
  getDocument: (type) => apiClient.get(`/legal-documents/${type}`),
  
  // Sauvegarder ou mettre à jour un document légal
  saveDocument: (type, content) => apiClient.post("/legal-documents", { type, content }),
};
